import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, BarChart3 } from 'lucide-react';
import { apiFetch } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await apiFetch(API_ROUTES.auth.forgotPassword, {
        method: 'POST',
        body: { email },
        auth: false,
      });
      if (res.ok) {
        setSent(true);
      } else {
        const data = await res.json();
        setError(data.detail || 'Something went wrong.');
      }
    } catch (e) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#070912] flex items-center justify-center p-4 relative overflow-hidden">
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[140px] animate-float-slow" />
          <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500/8 blur-[130px] animate-float" />
        </div>
        <div className="w-full max-w-md relative z-10 animate-scale-in">
          <div className="relative rounded-3xl p-[1px] bg-gradient-to-br from-emerald-400/30 via-emerald-400/10 to-transparent">
            <div className="rounded-3xl p-8 text-center backdrop-blur-xl" style={{
              background: 'linear-gradient(135deg, rgba(15,20,40,0.85), rgba(10,13,31,0.9))',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5), 0 0 60px rgba(16,185,129,0.1)',
            }}>
              <div className="w-20 h-20 mx-auto mb-5 rounded-2xl flex items-center justify-center" style={{
                background: 'rgba(16,185,129,0.12)',
                boxShadow: '0 0 40px rgba(16,185,129,0.3)',
              }}>
                <CheckCircle size={40} className="text-emerald-400" />
              </div>
              <h2 className="font-display text-2xl font-bold text-white mb-3">Reset link sent</h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                If an account with <span className="text-emerald-400 font-medium">{email}</span> exists,
                you'll receive a password reset link shortly.
              </p>
              <Link to="/login" className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-xl transition-all" style={{
                background: 'linear-gradient(135deg, #10b981, #14b8a6)',
                color: '#070912',
                boxShadow: '0 4px 20px rgba(16,185,129,0.3)',
              }}>
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070912] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[140px] animate-float-slow" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500/8 blur-[130px] animate-float" />
      </div>

      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        <Link to="/" className="flex flex-col items-center gap-3 mb-8 group">
          <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 via-teal-400 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-105 transition-transform">
            <BarChart3 size={26} className="text-white" />
          </div>
          <h1 className="font-display text-xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">TrackMyFxEA</h1>
        </Link>

        <div className="relative rounded-3xl p-[1px] bg-gradient-to-br from-white/15 via-white/5 to-transparent">
          <div className="rounded-3xl p-8 backdrop-blur-xl" style={{
            background: 'linear-gradient(135deg, rgba(15,20,40,0.85), rgba(10,13,31,0.9))',
            boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
          }}>
            <div className="mb-6">
              <h2 className="font-display text-2xl font-bold text-white mb-1">Forgot password?</h2>
              <p className="text-sm text-gray-500">Enter your email and we'll send you a reset link</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">Email</label>
                <div className="relative group">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600 group-focus-within:text-emerald-400 transition-colors" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-black/30 border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-400/50 focus:bg-black/40 focus:shadow-[0_0_0_3px_rgba(16,185,129,0.1)] transition-all backdrop-blur-sm"
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-xl px-4 py-3 text-red-400 text-sm animate-fade-in" style={{
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.25)',
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full font-bold py-3.5 rounded-xl transition-all duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #10b981, #14b8a6, #06b6d4)',
                  color: '#070912',
                  boxShadow: '0 4px 20px rgba(16,185,129,0.3)',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(16,185,129,0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(16,185,129,0.3)';
                }}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-[#070912] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Mail size={16} />
                    Send Reset Link
                  </>
                )}
              </button>
            </form>

            <Link to="/login" className="flex items-center justify-center gap-2 text-gray-500 text-sm mt-6 hover:text-gray-300 transition-colors">
              <ArrowLeft size={14} />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
